// In-app уведомления: контекст очереди тостов + звук (SPEC §4.5). Любой экран зовёт `notify({...})`;
// провайдер рисует стек тостов и сам их гасит по TTL. Звук — презентационный браузерный Audio (не
// сетевой), по настройке NotifySettings.sound. Решение «показывать ли тост по message.new» принимает
// useInAppNotifications (ниже) — он знает активный чат/фокус/настройки.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { TOAST_TTL, type Toast, type ToastInput } from './toast.js'

export interface NotificationsValue {
  toasts: Toast[]
  notify(input: ToastInput): string
  dismiss(id: string): void
  /** Проиграть короткий звук уведомления (если разрешено настройкой — решает вызывающий). */
  playSound(): void
}

const NotificationsContext = createContext<NotificationsValue | null>(null)

let toastSeq = 0
function nextToastId(): string {
  toastSeq += 1
  return `toast-${toastSeq}-${Date.now()}`
}

export interface NotificationsProviderProps {
  children: ReactNode
  /** Инъекция времени/таймеров для тестов. */
  now?: () => number
}

export function NotificationsProvider({ children, now }: NotificationsProviderProps): JSX.Element {
  // Стабильная идентичность clock: без useMemo `() => Date.now()` пересоздавался КАЖДЫЙ рендер →
  // notify (useCallback c [clock]) менял идентичность → эффекты, зависящие от notify (напр. показ
  // тоста «доступно обновление» в AppShell), перезапускались на каждом рендере, а добавление тоста
  // вызывало ре-рендер → БЕСКОНЕЧНАЯ петля тостов. Мемоизация рвёт петлю.
  const clock = useMemo(() => now ?? (() => Date.now()), [now])
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: string): void => {
    setToasts((list) => list.filter((t) => t.id !== id))
    const handle = timers.current.get(id)
    if (handle !== undefined) {
      clearTimeout(handle)
      timers.current.delete(id)
    }
  }, [])

  const notify = useCallback(
    (input: ToastInput): string => {
      const id = nextToastId()
      const ttlMs = input.ttlMs ?? TOAST_TTL[input.kind]
      const toast: Toast = { ...input, id, ttlMs, createdAt: clock() }
      // Дедуп по dedupeKey: тост с тем же ключом ЗАМЕНЯЕТ прежний (не плодит копии — напр. один
      // «доступно обновление», а не стопка). Без ключа — обычный append.
      setToasts((list) => {
        const base = input.dedupeKey ? list.filter((t) => t.dedupeKey !== input.dedupeKey) : list
        return [...base, toast]
      })
      if (ttlMs > 0) {
        const handle = setTimeout(() => dismiss(id), ttlMs)
        timers.current.set(id, handle)
      }
      return id
    },
    [clock, dismiss],
  )

  const playSound = useCallback((): void => {
    // Короткий beep через WebAudio — без сетевого ассета. Деградирует тихо, если AudioContext недоступен.
    try {
      const Ctor =
        typeof window !== 'undefined'
          ? (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)
          : undefined
      if (!Ctor) return
      const ctx = new Ctor()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = 880
      gain.gain.value = 0.04
      osc.connect(gain).connect(ctx.destination)
      osc.start()
      osc.stop(ctx.currentTime + 0.12)
      osc.onended = () => void ctx.close()
    } catch {
      // нет аудио — деградируем без звука
    }
  }, [])

  useEffect(() => {
    const map = timers.current
    return () => {
      for (const handle of map.values()) clearTimeout(handle)
      map.clear()
    }
  }, [])

  const value = useMemo<NotificationsValue>(
    () => ({ toasts, notify, dismiss, playSound }),
    [toasts, notify, dismiss, playSound],
  )

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
}

/** Доступ к очереди уведомлений. Бросает вне NotificationsProvider. */
export function useNotifications(): NotificationsValue {
  const ctx = useContext(NotificationsContext)
  if (ctx === null) {
    throw new Error('useNotifications(): компонент должен быть внутри <NotificationsProvider>')
  }
  return ctx
}
